/**
 * Turning a balance into a decision.
 *
 * "240 credits left" is a number nobody can act on. "240 left — about 8 days
 * at your current rate" is: it says whether to top up now or ignore it. The
 * forecast is deliberately coarse (a week's spend, divided) because it is a
 * prompt, not an invoice, and precision would imply a promise we can't keep.
 */

export interface CreditForecast {
  /** Average credits per day over the last week. */
  perDay: number;
  /** Whole days of runway left at that rate. */
  daysLeft: number;
  /** Runway short enough to be worth saying out loud unprompted. */
  urgent: boolean;
}

/** Below this many days of runway, the forecast is worth surfacing. */
const URGENT_DAYS = 10;

/**
 * Returns null when there is nothing honest to say: no spending to
 * extrapolate from, or no balance to run out of.
 */
export function creditForecast(input: {
  balance: number;
  spent7d: number;
}): CreditForecast | null {
  const { balance, spent7d } = input;
  if (!Number.isFinite(balance) || !Number.isFinite(spent7d)) return null;
  if (spent7d <= 0 || balance <= 0) return null;

  const perDay = spent7d / 7;
  // Floor, not round: promising a tenth day and delivering nine reads as a
  // broken promise, while under-promising never does.
  const daysLeft = Math.max(0, Math.floor(balance / perDay));
  return { perDay, daysLeft, urgent: daysLeft <= URGENT_DAYS };
}

/** The free monthly credits inside a balance, for "incl. 640 free credits until Nov 1". */
export interface FreeCreditSplit {
  free: number;
  /** When the free bucket resets (and leftovers expire); null when unknown. */
  resetAt: Date | null;
}

/**
 * Null when the account has no monthly allowance or the backend predates the
 * free-monthly fields: callers then show the total only.
 */
export function freeCreditSplit(input: {
  freeMonthly?: number | null;
  freeMonthlyAllowance?: number | null;
  nextFreeReset?: string | null;
}): FreeCreditSplit | null {
  const { freeMonthly, freeMonthlyAllowance, nextFreeReset } = input;
  if (typeof freeMonthly !== 'number' || !Number.isFinite(freeMonthly)) return null;
  if (typeof freeMonthlyAllowance !== 'number' || !(freeMonthlyAllowance > 0)) return null;
  const at = nextFreeReset ? new Date(nextFreeReset) : null;
  return {
    free: Math.max(0, freeMonthly),
    resetAt: at && !Number.isNaN(at.getTime()) ? at : null,
  };
}

/** "1. Nov." / "Nov 1", in Berlin time: the reset is midnight there. */
export function formatFreeReset(at: Date, language: string | undefined): string {
  return at.toLocaleDateString(language?.startsWith('de') ? 'de-DE' : 'en-US', {
    day: 'numeric',
    month: 'short',
    timeZone: 'Europe/Berlin',
  });
}
